// ============================================
// MODIFY HOOKS
// Combines hooks from:
// - [BuilderModify.tsx] BuilderModify (MODIFY 650-662)
// - [BuilderModifyFields.tsx] -> [BuilderModify.tsx] (MODIFY 663)
// Note: Most hooks in BuilderModify.tsx and BuilderModifyFields.tsx are inline
// useEffect hooks tightly coupled to their components and cannot be extracted.
// Only builderNoteToFields is a pure function that can be moved here.
// ============================================

import {builderFieldsType} from '../CreateBuilder/builderFields';
import type {
  modelPortfolioField,
  modifyFieldsType,
} from '../BuilderModify/builderModifyFieldTypes';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {Pot} from '../../../schema-types/Pot';
import {getPlanFeesFormikFromServer} from '../CreateBuilder/BuilderPlanFees';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {AutoFieldHelpers} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {findParentOfChild} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';

export enum builderModifyChanges {
  feeChange = 'Change in fee',
  mandateChange = 'Change in mandate',
  regularContributionChange = 'Change in regular contribution',
  regularWithdrawalChange = 'Change in regular withdrawal',
  changeOfOwnership = 'Change in ownership',
}

export function builderNoteToFields(
  recommendation: OVRecommendation,
  sourcePots: Pot[]
): builderFieldsType['modify'] {
  if ((recommendation.action ?? undefined) === OVRecommendActionEnum.MODIFY) {
    let noteFull = String(recommendation.note || '');
    const pot = recommendation.pots?.length ? recommendation.pots[0] : null;
    const sourcePot = pot?.id ? sourcePots.find((x) => x.id === pot?.id) : null;
    const feeChange = noteFull?.includes(builderModifyChanges.feeChange);
    if (feeChange) {
      noteFull = noteFull.replaceAll(builderModifyChanges.feeChange, '');
    }
    const mandateChange =
      noteFull?.includes(builderModifyChanges.mandateChange) ||
      !!(sourcePot?.mandate || sourcePot?.modelPortfolio?.id);

    const planFees =
      mandateChange && recommendation?.pots?.length
        ? getPlanFeesFormikFromServer(recommendation?.pots[0])
        : null;

    if (mandateChange) {
      noteFull = noteFull.replaceAll(builderModifyChanges.mandateChange, '');
    }
    const regularContributionChange = noteFull?.includes(
      builderModifyChanges.regularContributionChange
    );
    if (regularContributionChange) {
      noteFull = noteFull.replaceAll(builderModifyChanges.regularContributionChange, '');
    }
    const regularWithdrawalChange = noteFull?.includes(
      builderModifyChanges.regularWithdrawalChange
    );
    if (regularWithdrawalChange) {
      noteFull = noteFull.replaceAll(builderModifyChanges.regularWithdrawalChange, '');
    }
    const changeOfOwnership = noteFull?.includes(builderModifyChanges.changeOfOwnership);
    if (changeOfOwnership) {
      noteFull = noteFull.replaceAll(builderModifyChanges.changeOfOwnership, '');
    }
    const model: modelPortfolioField | null = pot?.modelPortfolio
      ? {
          ...pot?.modelPortfolio,
          providerContactLastName:
            pot.modelPortfolio.providerContact?.lastName || undefined,
          providerContactJobTitle:
            pot.modelPortfolio.providerContact?.jobTitle || undefined,
        }
      : null;

    return {
      feeChange,
      mandateChange,
      note: noteFull.trim(),
      regularContributionChange,
      regularWithdrawalChange,
      changeOfOwnership,
      source: {
        id: recommendation?.pots?.length ? recommendation.pots[0].pot?.id ?? '' : '',
      },
      model: pot?.mandate || model || undefined,
      planFees,
    };
  }

  return {
    feeChange: false,
    mandateChange: false,
    regularContributionChange: false,
    regularWithdrawalChange: false,
    changeOfOwnership: false,
    note: '',
    source: {
      id: '',
    },
    model: undefined,
  };
}

export const modifySources = (sourcePots: BuilderPot[]): BuilderPot[] =>
  sourcePots.filter((el) => !el.isDummy && !!el.curoId);

const defaultModifyValues: builderFieldsType['modify'] = {
  feeChange: false,
  mandateChange: false,
  regularContributionChange: false,
  regularWithdrawalChange: false,
  changeOfOwnership: false,
  note: '',
  source: {id: ''},
  model: undefined,
};

export const getModifyInitialValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): builderFieldsType['modify'] => {
  const savedPot = builderDetails?.modify?.pot;
  if (!savedPot) return defaultModifyValues;

  const parentPotId = savedPot.parent?.id;
  const note = builderDetails!.note ?? '';

  const feeChange = note.includes(builderModifyChanges.feeChange);
  const mandateChange = note.includes(builderModifyChanges.mandateChange);
  const regularContributionChange = note.includes(
    builderModifyChanges.regularContributionChange
  );
  const regularWithdrawalChange = note.includes(
    builderModifyChanges.regularWithdrawalChange
  );
  const changeOfOwnership = note.includes(builderModifyChanges.changeOfOwnership);
  const cleanNote = [
    builderModifyChanges.feeChange,
    builderModifyChanges.mandateChange,
    builderModifyChanges.regularContributionChange,
    builderModifyChanges.regularWithdrawalChange,
    builderModifyChanges.changeOfOwnership,
  ]
    .reduce((n, s) => n.replaceAll(s, ''), note)
    .trim();

  const savedModel =
    builderDetails!.modify?.nonSaltusMandate ||
    builderDetails!.modify?.modelPortfolio ||
    undefined;

  return {
    feeChange,
    mandateChange,
    regularContributionChange,
    regularWithdrawalChange,
    changeOfOwnership,
    note: cleanNote,
    source: {
      id: savedPot.id,
      isChild: !!parentPotId,
      parentPotId,
    },
    model: savedModel as modelPortfolioField | undefined,
    planFees: null,
  };
};

export const changeModifySourceFactory = ({
  sourcePots,
  modifySourceHelpers,
}: {
  sourcePots: BuilderPot[];
  modifySourceHelpers: AutoFieldHelpers<modifyFieldsType['source'] | undefined>;
}) => {
  return (value: string) => {
    const parent = findParentOfChild(sourcePots, value);
    if (parent) {
      modifySourceHelpers.setValue(UPDATES._D662, {
        id: value,
        isChild: true,
        isSaltus: !!parent.isSaltus,
        parentPotId: parent.id,
      });
      return;
    }
    const leafPot = sourcePots.find((p) => p.id === value);
    if (!leafPot) return;
    modifySourceHelpers.setValue(UPDATES._D662, {
      id: value,
      isChild: false,
      isSaltus: !!leafPot.isSaltus,
    });
  };
};
